import React from "react";
import PropTypes from "prop-types";

import { EventEmitter } from "./event.emitter.js";

export const INFINITE_SCROLL_AXIS = {
  X: "x",
  Y: "y"
};

InfiniteContainer.propTypes = {
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  scrollElemSelector: PropTypes.string.isRequired,
  scrollOffset: PropTypes.number.isRequired,
  axis: PropTypes.string
};

export function InfiniteContainer({
  children,
  scrollElemSelector,
  scrollOffset = 200,
  axis = INFINITE_SCROLL_AXIS.Y
}) {
  let [observer, setObserver] = React.useState(null);
  let [eventManager, setEventManager] = React.useState(null);
  let visibilityCache = React.useRef({});

  let getVisibility = React.useCallback(function getVisibilityCallback(itemId) {
    return visibilityCache.current[itemId];
  }, []);

  React.useEffect(
    function initializeObservers() {
      let scrollElem =
        document.querySelector(
          `${scrollElemSelector} .ScrollbarsCustom-Scroller`
        ) || document.querySelector(scrollElemSelector);
      if (!scrollElem) {
        console.warn("No scroll parent element found");
        return;
      }

      function setVisibility(itemId, visibility) {
        visibilityCache.current[itemId] = visibility;
      }

      function observerCallback(entries) {
        entries.forEach((entry) => {
          let itemId = entry.target.getAttribute("id");
          let itemHeight = `${entry.target.offsetHeight}px`;
          let itemWidth = `${entry.target.offsetWidth}px`;
          if (entry.isIntersecting) {
            setVisibility(itemId, true);
            eventManager.emit("visibilityChange", {
              visibility: true,
              itemId,
              itemHeight,
              itemWidth
            });
          } else if (!entry.isIntersecting && entry.intersectionRatio <= 0) {
            setVisibility(itemId, false);
            eventManager.emit("visibilityChange", {
              visibility: false,
              itemId,
              itemHeight,
              itemWidth
            });
          }
        });
      }

      let options = {
        root: scrollElem,
        rootMargin:
          axis === INFINITE_SCROLL_AXIS.Y
            ? `${scrollOffset}px 0px`
            : `0px ${scrollOffset}px`,
        threshold: 0
      };
      let observer = new IntersectionObserver(observerCallback, options);
      setObserver(observer);

      let eventManager = new EventEmitter();
      setEventManager(eventManager);

      return () => observer.disconnect();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scrollElemSelector, scrollOffset]
  );

  return observer
    ? children({
        Observer: observer,
        EventManager: eventManager,
        getVisibility,
        axis
      })
    : null;
}

InfiniteElement.propTypes = {
  Observer: PropTypes.shape({
    trackVisibility: PropTypes.bool,
    root: PropTypes.element
  }),
  EventManager: PropTypes.shape({
    events: PropTypes.shape({ visibilityChange: PropTypes.func })
  }),
  getVisibility: PropTypes.func,
  itemId: PropTypes.string,
  Wrapper: PropTypes.string,
  minHeight: PropTypes.string,
  minWidth: PropTypes.string,
  children: PropTypes.node,
  axis: PropTypes.string,
  pageStyle: PropTypes.string
};

export function InfiniteElement({
  Observer,
  EventManager,
  getVisibility,
  itemId,
  Wrapper = "div",
  minHeight = "3rem",
  minWidth = "2rem",
  children = null,
  axis = INFINITE_SCROLL_AXIS.Y,
  pageStyle,
  ...rest
}) {
  let [showChild, toggleShowChild] = React.useState(false);

  let style = axis === INFINITE_SCROLL_AXIS.X ? { minWidth } : { minHeight };

  let [itemStyle, setItemStyle] = React.useState(style);

  let itemRef = React.createRef();

  React.useEffect(
    function observeItem() {
      let itemNode = itemRef.current;
      itemNode && Observer.observe(itemNode);
      return () => Observer.unobserve(itemNode);
    },
    [Observer, itemRef]
  );

  React.useEffect(
    function onVisibilityChange() {
      function callback({ visibility, itemId: id, itemHeight, itemWidth }) {
        if (itemId !== id) {
          return;
        }
        requestAnimationFrame(() => {
          toggleShowChild(getVisibility(id));
          if (
            axis === INFINITE_SCROLL_AXIS.Y &&
            itemHeight &&
            showChild !== getVisibility(id)
          ) {
            setItemStyle(
              Object.assign({}, itemStyle, {
                minHeight: getVisibility(id) ? "auto" : itemHeight
              })
            );
          }
          if (
            axis === INFINITE_SCROLL_AXIS.X &&
            itemWidth &&
            showChild !== getVisibility(id)
          ) {
            setItemStyle(
              Object.assign({}, itemStyle, {
                minWidth: getVisibility(id) ? "auto" : itemWidth
              })
            );
          }
        });
        //Todo set element height based on its content it ir is visible
      }
      EventManager.on("visibilityChange", callback);
      return () => {
        EventManager.removeListener("visibilityChange", callback);
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [EventManager, itemId, itemStyle, getVisibility]
  );

  return (
    <Wrapper
      id={itemId}
      ref={itemRef}
      className={pageStyle}
      data-pageindex={itemId}
      style={itemStyle}
      {...rest}
    >
      {typeof children === "function"
        ? children({ isVisible: showChild })
        : showChild && children}
    </Wrapper>
  );
}
